import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import SEO from '@components/common/SEO'
import SearchBar from '@components/common/SearchBar'
import CompanyCard from '@components/company/CompanyCard'
import Pagination from '@components/common/Pagination'
import MapView from '@components/map/MapView'
import { CustomAdBanner } from '@components/ads'
import { useSearchCompanies, useProvinces, useNearbyCompanies } from '@/hooks/useApi'
import { useServiceTypes } from '@/hooks/useServiceTypes'
import { Company, ApiService, NearbySearchFilters } from '@/services/api'
import { MagnifyingGlassIcon, FunnelIcon, ListBulletIcon, MapIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/contexts/AuthContext'

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFilters, setShowFilters] = useState(false)

  // Read view mode from URL (?view=map or ?view=list)
  const viewParam = searchParams.get('view')
  const initialViewMode = (viewParam === 'map' || viewParam === 'list') ? viewParam : 'list'
  const [viewMode, setViewMode] = useState<'list' | 'map'>(initialViewMode)

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const { isAuthenticated, isAdmin, isMember, isGuest } = useAuth()

  // Get search parameters
  const query = searchParams.get('q') || ''
  const province = searchParams.get('province') || ''
  const serviceType = searchParams.get('serviceType') || ''
  const companySize = searchParams.get('companySize') || ''
  const featured = searchParams.get('featured') === 'true'
  const sortBy = searchParams.get('sortBy') || 'relevance'
  const currentPage = parseInt(searchParams.get('page') || '1', 10)
  const showFiltersParam = searchParams.get('showFilters') === 'true'
  const focusFilter = searchParams.get('focusFilter')
  const focusSearchParam = searchParams.get('focusSearch') === 'true'

  // Convert sortBy option to API parameters
  const getSortParams = (sortOption: string): { sortBy?: string; sortOrder: 'asc' | 'desc' } => {
    const sortMap: Record<string, { sortBy?: string; sortOrder: 'asc' | 'desc' }> = {
      'relevance': { sortOrder: 'desc' }, // Default relevance (updated_at desc)
      'name-asc': { sortBy: 'nameTh', sortOrder: 'asc' },
      'name-desc': { sortBy: 'nameTh', sortOrder: 'desc' },
      'founded-newest': { sortBy: 'establishedYear', sortOrder: 'desc' },
      'founded-oldest': { sortBy: 'establishedYear', sortOrder: 'asc' },
      'views-most': { sortBy: 'viewCount', sortOrder: 'desc' },
    }
    return sortMap[sortOption] || { sortOrder: 'desc' }
  }

  const sortParams = getSortParams(sortBy)

  // GPS Request Function
  const requestUserLocation = () => {
    setIsLoadingLocation(true)
    setLocationError(null)

    if (!navigator.geolocation) {
      setLocationError('เบราว์เซอร์ของคุณไม่รองรับ Geolocation')
      setIsLoadingLocation(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        setViewMode('map')
        // Update URL to map view
        const newParams = new URLSearchParams(searchParams)
        newParams.set('view', 'map')
        setSearchParams(newParams)
        setIsLoadingLocation(false)
      },
      (error) => {
        console.error('Geolocation error:', error)
        let errorMessage = 'ไม่สามารถเข้าถึงตำแหน่งของคุณได้'

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'คุณปฏิเสธการเข้าถึงตำแหน่ง กรุณาอนุญาตในการตั้งค่าเบราว์เซอร์'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'ไม่สามารถระบุตำแหน่งของคุณได้'
            break
          case error.TIMEOUT:
            errorMessage = 'หมดเวลาในการระบุตำแหน่ง'
            break
        }

        setLocationError(errorMessage)
        setIsLoadingLocation(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      }
    )
  }

  // Build nearby search filters
  const nearbyFilters: NearbySearchFilters | null = userLocation ? {
    lat: userLocation.lat,
    lng: userLocation.lng,
    radius_km: 10, // Default 10km
    province: province || undefined,
    service_types: serviceType ? [serviceType] : undefined,
    company_size: companySize || undefined,
    is_featured: featured || undefined,
    page: currentPage,
    per_page: 20,
  } : null

  // DEBUG: Log filter values to verify they're being passed correctly
  useEffect(() => {
    if (viewMode === 'map' && nearbyFilters) {
      console.log('🗺️ [Map Mode] nearbyFilters:', {
        province: nearbyFilters.province,
        company_size: nearbyFilters.company_size,
        service_types: nearbyFilters.service_types,
        is_featured: nearbyFilters.is_featured,
      });
    }
  }, [viewMode, nearbyFilters, companySize])

  // API calls using React Query
  const { data: searchResults, isLoading, error } = useSearchCompanies(
    query,
    {
      province: province || undefined,
      serviceType: serviceType || undefined,
      companySize: companySize || undefined,
      featured: featured || undefined,
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
    },
    currentPage
  )

  // Nearby search (for map mode)
  const { data: nearbyResults, isLoading: isLoadingNearby, error: nearbyError } = useNearbyCompanies(
    viewMode === 'map' ? nearbyFilters : null
  )

  const { data: provinces = [] } = useProvinces()

  // Fetch service types from API
  const { serviceTypes: serviceTypesData, loading: loadingServiceTypes } = useServiceTypes(true)

  // Extract service type names for dropdown
  const serviceTypes = serviceTypesData.map(st => st.nameTh)

  // Extract companies from search results based on view mode
  const companies = viewMode === 'map'
    ? (nearbyResults?.companies || [])
    : (searchResults?.companies || [])

  const totalResults = viewMode === 'map'
    ? (nearbyResults?.total || 0)
    : (searchResults?.total || 0)

  const totalPages = viewMode === 'map'
    ? (nearbyResults?.total_pages || 1)
    : (searchResults?.total_pages || 1)

  const currentLoading = viewMode === 'map' ? isLoadingNearby : isLoading
  const currentError = viewMode === 'map' ? nearbyError : error

  // Track search when results are available
  useEffect(() => {
    if (searchResults && query.trim()) {
      const filters = {
        province: province || undefined,
        serviceType: serviceType || undefined,
        companySize: companySize || undefined,
        featured: featured || undefined,
      }

      // Track the search with timing information
      ApiService.trackSearch(
        query,
        totalResults,
        filters,
        searchResults.search_time_ms
      )
    }
  }, [searchResults, query, province, serviceType, companySize, featured, totalResults])

  // Auto-request GPS if page loads with ?view=map
  useEffect(() => {
    if (initialViewMode === 'map' && !userLocation && !isLoadingLocation && !locationError) {
      console.log('📍 Auto-requesting GPS for direct map view access')
      requestUserLocation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount

  // Auto-open filters panel and focus on specified filter
  useEffect(() => {
    if (showFiltersParam) {
      setShowFilters(true)

      // Focus on the specified filter after a short delay to ensure DOM is ready
      if (focusFilter) {
        setTimeout(() => {
          const filterElement = document.getElementById(`${focusFilter}-filter`)
          if (filterElement) {
            filterElement.focus()
            // Scroll into view if needed
            filterElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      }
    }
  }, [showFiltersParam, focusFilter, searchParams])

  const handleFilterChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    // Reset to page 1 when filters change
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  const clearAllFilters = () => {
    const newParams = new URLSearchParams()
    if (query) newParams.set('q', query)
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', String(page))
    setSearchParams(newParams)
  }

  const handleSortChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value && value !== 'relevance') {
      newParams.set('sortBy', value)
    } else {
      newParams.delete('sortBy')
    }
    // Reset to page 1 when sort changes
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  const activeFiltersCount = [province, serviceType, companySize].filter(Boolean).length + (featured ? 1 : 0)

  // Helper function to build company detail URL with preserved search context
  const buildCompanyDetailUrl = (slug: string): string => {
    const params = new URLSearchParams()
    params.set('from', 'search')
    params.set('view', viewMode)

    // Preserve all search/filter parameters
    if (query) params.set('q', query)
    if (province) params.set('province', province)
    if (serviceType) params.set('serviceType', serviceType)
    if (companySize) params.set('companySize', companySize)
    if (featured) params.set('featured', 'true')
    if (sortBy && sortBy !== 'relevance') params.set('sortBy', sortBy)
    if (currentPage > 1) params.set('page', String(currentPage))

    return `/companies/${slug}?${params.toString()}`
  }

  return (
    <>
      <SEO
        title={`ค้นหา${query ? `: ${query}` : ''}`}
        description={`ค้นหาบริษัทรักษาความปลอดภัยในประเทศไทย${query ? ` - ${query}` : ''}`}
      />

      <div className="container-wrapper py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <SearchBar
            initialValue={query}
            autoFocus={focusSearchParam}
            size="lg"
            variant="floating"
            placeholder="ค้นหาบริษัทรักษาความปลอดภัย เช่น ชื่อบริษัท, จังหวัด, ประเภทบริการ"
          />
        </div>

        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">
            {query ? `ผลการค้นหา: "${query}"` : 'ผลการค้นหา'}
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Results Count */}
            {!currentLoading && !currentError && (
              <p className="text-neutral-600">
                พบ {totalResults.toLocaleString()} บริษัท
                {query && ` สำหรับ "${query}"`}
                {viewMode === 'map' && userLocation && ' ในบริเวณใกล้เคียง'}
              </p>
            )}

            {currentError && (
              <p className="text-red-600">
                เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง
              </p>
            )}

            {/* Sort Dropdown (List mode only) */}
            {!currentLoading && !currentError && companies.length > 0 && viewMode === 'list' && (
              <div className="flex items-center gap-2">
                <label htmlFor="sort-select" className="form-label mb-0 text-sm font-medium text-neutral-700">
                  เรียงตาม:
                </label>
                <select
                  id="sort-select"
                  className="form-input py-2 text-sm min-w-[200px]"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <option value="relevance">ความเกี่ยวข้อง</option>
                  <option value="name-asc">ชื่อ A-Z</option>
                  <option value="name-desc">ชื่อ Z-A</option>
                  <option value="founded-newest">ปีก่อตั้งใหม่สุด</option>
                  <option value="founded-oldest">ปีก่อตั้งเก่าสุด</option>
                  <option value="views-most">ผู้เข้าชมมากสุด</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setViewMode('list')
                const newParams = new URLSearchParams(searchParams)
                newParams.set('view', 'list')
                setSearchParams(newParams)
              }}
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            >
              <ListBulletIcon className="w-4 h-4" />
              รายการ
            </button>
            <button
              onClick={() => {
                if (!userLocation) {
                  requestUserLocation()
                } else {
                  setViewMode('map')
                  const newParams = new URLSearchParams(searchParams)
                  newParams.set('view', 'map')
                  setSearchParams(newParams)
                }
              }}
              className={`btn ${viewMode === 'map' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
              disabled={isLoadingLocation}
            >
              <MapIcon className="w-4 h-4" />
              {isLoadingLocation ? 'กำลังขออนุญาต...' : 'แผนที่'}
            </button>
          </div>

          {/* Location Permission Error */}
          {locationError && (
            <div className="flex-1 sm:max-w-md p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              {locationError}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <FunnelIcon className="w-4 h-4" />
              ตัวกรอง
              {activeFiltersCount > 0 && (
                <span className="badge badge-primary text-xs ml-1">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                ล้างตัวกรองทั้งหมด
              </button>
            )}
          </div>

          {showFilters && (
            <div className="bg-white rounded-lg border border-neutral-200 p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Province Filter */}
              <div>
                <label className="form-label">จังหวัด</label>
                <select
                  id="province-filter"
                  className="form-input"
                  value={province}
                  onChange={(e) => handleFilterChange('province', e.target.value)}
                >
                  <option value="">ทุกจังหวัด</option>
                  {provinces.map((prov) => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                </select>
              </div>

              {/* Service Type Filter */}
              <div>
                <label className="form-label">ประเภทบริการ</label>
                <select
                  id="serviceType-filter"
                  className="form-input"
                  value={serviceType}
                  onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                >
                  <option value="">ทุกประเภท</option>
                  {serviceTypes.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              {/* Company Size Filter */}
              <div>
                <label className="form-label">ขนาดบริษัท</label>
                <select
                  className="form-input"
                  value={companySize}
                  onChange={(e) => handleFilterChange('companySize', e.target.value)}
                >
                  <option value="">ทุกขนาด</option>
                  <option value="SMALL">เล็ก</option>
                  <option value="MEDIUM">กลาง</option>
                  <option value="LARGE">ใหญ่</option>
                </select>
              </div>

              {/* Featured Filter */}
              <div>
                <label className="form-label">บริษัทแนะนำ</label>
                <label className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={featured}
                    onChange={(e) => handleFilterChange('featured', e.target.checked ? 'true' : '')}
                    className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm">แสดงเฉพาะบริษัทแนะนำ</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="space-y-6">
          {currentError ? (
            // Error State
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                เกิดข้อผิดพลาดในการค้นหา
              </h3>
              <p className="text-neutral-600 mb-6">
                ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
              >
                ลองใหม่
              </button>
            </div>
          ) : currentLoading ? (
            // Loading State
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-neutral-600">กำลังค้นหา...</span>
            </div>
          ) : viewMode === 'map' && !userLocation ? (
            // Map Mode but no GPS permission yet
            <div className="text-center py-12">
              <MapIcon className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                {isLoadingLocation ? 'กำลังขออนุญาตเข้าถึงตำแหน่ง...' : 'เปิดใช้งานแผนที่'}
              </h3>
              <p className="text-neutral-600 mb-6">
                {isLoadingLocation
                  ? 'กรุณารอสักครู่...'
                  : 'คลิกปุ่มด้านล่างเพื่อขออนุญาตเข้าถึงตำแหน่งของคุณและแสดงบริษัทใกล้เคียง'
                }
              </p>
              {!isLoadingLocation && (
                <button
                  onClick={requestUserLocation}
                  className="btn btn-primary"
                  disabled={isLoadingLocation}
                >
                  เปิดใช้งานแผนที่
                </button>
              )}
            </div>
          ) : viewMode === 'map' && userLocation ? (
            // Map View
            <MapView
              companies={companies}
              userLocation={userLocation}
              showContactInfo={isAuthenticated && !isGuest}
              buildDetailUrl={buildCompanyDetailUrl}
              zoom={12}
              height="600px"
            />
          ) : companies.length > 0 ? (
            // Results List
            <>
              {companies.map((company, index) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  showContactInfo={isAuthenticated && !isGuest}
                  detailUrl={buildCompanyDetailUrl(company.slug)}
                />
              ))}
            </>
          ) : (
            // No Results with Advertisement
            <div className="space-y-8">
              <div className="text-center py-12">
                <MagnifyingGlassIcon className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">
                  ไม่พบผลการค้นหา
                </h3>
                <p className="text-neutral-600 mb-6">
                  ลองค้นหาด้วยคำอื่น หรือปรับเปลี่ยนตัวกรอง
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-neutral-500">ลองค้นหา:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['รักษาความปลอดภัย', 'ยามรักษาการณ์', 'รปภ.', 'security'].map((term) => (
                      <button
                        key={term}
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams)
                          newParams.set('q', term)
                          setSearchParams(newParams)
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700 underline"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Advertisement when no results found */}
              <div className="flex justify-center">
                <CustomAdBanner
                  type="in-feed"
                  imageUrl="/ads/koder3-banner-wide.jpg"
                  targetUrl="https://www.koder3.com"
                  alt="ระบบจัดการบริษัทรักษาความปลอดภัย - Koder3"
                  className="max-w-7xl mx-auto"
                  showLabel={true}
                />
              </div>
            </div>
          )}
        </div>

        {/* Pagination & Results Footer (List mode only) */}
        {!currentLoading && !currentError && companies.length > 0 && viewMode === 'list' && (
          <div className="mt-12 space-y-6">
            {/* Pagination Controls */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="mb-6"
            />

            {/* Results Summary */}
            <div className="text-center">
              <p className="text-neutral-600">
                แสดงผล {companies.length} จาก {totalResults.toLocaleString()} บริษัท
                {totalPages > 1 && ` (หน้า ${currentPage} จาก ${totalPages})`}
              </p>
            </div>

            {/* 🖼️ Advertisement - Bottom of page */}
            <div className="mt-12 flex justify-center">
              <CustomAdBanner
                type="in-feed"
                imageUrl="/ads/koder3-banner-wide.jpg"
                targetUrl="https://www.koder3.com"
                alt="ระบบจัดการบริษัทรักษาความปลอดภัย - Koder3"
                className="max-w-7xl mx-auto"
                showLabel={true}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default SearchPage